'use client'

import { IconFire } from '~/icons/ic_fire'
import RoomTypeBanner from '../../shared/room-type-banner'
import type { RoomType } from '../../shared/type'
import { useState, useEffect, useCallback, memo } from 'react'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
import RoomHeader from './room-header'
import RoomAmenities from './room-amenities'
import RoomFooter from './room-footer'

interface RoomTypeCardProps {
  roomType: RoomType
  onQuantityChange: (roomType: RoomType, quantity: number) => void
  initialQuantity?: number
  onViewDetails?: (roomType: RoomType) => void
}

function RoomTypeCard({
  roomType,
  onQuantityChange,
  initialQuantity = 0,
  onViewDetails,
}: RoomTypeCardProps) {
  const [quantity, setQuantity] = useState(initialQuantity)
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const allImages = [roomType?.imageUrl, ...roomType?.images?.map((img) => img.imageUrl)].filter(
    (url) => url && url.trim() !== ''
  )

  const handleDecrease = useCallback(() => {
    if (quantity > 0) {
      const newQuantity = quantity - 1
      setQuantity(newQuantity)
      onQuantityChange(roomType, newQuantity)
    }
  }, [quantity, roomType, onQuantityChange])

  const handleIncrease = useCallback(() => {
    if (quantity < roomType.available) {
      const newQuantity = quantity + 1
      setQuantity(newQuantity)
      onQuantityChange(roomType, newQuantity)
    }
  }, [quantity, roomType, onQuantityChange])

  const handleQuantityChange = useCallback(
    (val: number) => {
      setQuantity(val)
      onQuantityChange(roomType, val)
    },
    [roomType, onQuantityChange]
  )

  const handleViewDetails = useCallback(() => {
    onViewDetails?.(roomType)
  }, [roomType, onViewDetails])

  useEffect(() => {
    setQuantity(initialQuantity)
  }, [initialQuantity])

  return (
    <div className="relative flex h-full flex-col overflow-hidden rounded-[4px] border border-[#E5E6E8] bg-white">
      {/* Featured tag */}
      {roomType.label && (
        <div className="absolute left-0 top-0 z-10">
          <div className="rounded-lt-[4px] flex items-center gap-1 bg-[#E02424] p-[6px]">
            <IconFire className="h-4 w-4 text-white" />
            <p className="font-lexend text-xs leading-4 text-white">{t('roomType.featured')}</p>
          </div>
        </div>
      )}
      <RoomTypeBanner images={allImages} showSlider={false} onClick={handleViewDetails} />
      {/* Room details */}
      <div className="flex flex-col gap-2 px-4 py-3">
        <RoomHeader
          roomTypeName={roomType.roomTypeName}
          roomSize={roomType.roomSize}
          maxAdult={roomType.baseAdult}
          maxChild={roomType.baseChild}
          bedType={roomType.bedType}
          onViewDetails={handleViewDetails}
          t={t}
        />

        <RoomAmenities roomServices={roomType.roomServices} />

        <div className="my-2 h-[1px] w-full bg-[#F3F3F3]" />

        <RoomFooter
          price={roomType.price}
          available={roomType.available}
          quantity={quantity}
          onDecrease={handleDecrease}
          onIncrease={handleIncrease}
          onQuantityChange={handleQuantityChange}
          t={t}
        />
      </div>
    </div>
  )
}

function arePropsEqual(prevProps: RoomTypeCardProps, nextProps: RoomTypeCardProps) {
  return (
    prevProps.initialQuantity === nextProps.initialQuantity &&
    prevProps.roomType.price === nextProps.roomType.price &&
    prevProps.roomType.available === nextProps.roomType.available
  )
}

export default memo(RoomTypeCard, arePropsEqual)
